import { useCallback, useRef } from "react";

interface UseLongPressOptions {
  onLongPress: () => void;
  threshold?: number;
  moveThreshold?: number;
  disabled?: boolean;
}

interface UseLongPressReturn {
  handlers: {
    onPointerDown: (e: React.PointerEvent) => void;
    onPointerUp: () => void;
    onPointerCancel: () => void;
    onPointerLeave: () => void;
    onPointerMove: (e: React.PointerEvent) => void;
    onClick: (e: React.MouseEvent) => void;
    onContextMenu: (e: React.MouseEvent) => void;
  };
  style: React.CSSProperties;
}

export default function useLongPress({
  onLongPress,
  threshold = 500,
  moveThreshold = 25,
  disabled = false
}: UseLongPressOptions): UseLongPressReturn {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const startPos = useRef<{ x: number; y: number } | null>(null);
  const firedRef = useRef(false);

  const cancel = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    startPos.current = null;
  }, []);

  const onPointerDown = useCallback(
    (e: React.PointerEvent) => {
      if (disabled) {
        return;
      }
      firedRef.current = false;
      startPos.current = { x: e.clientX, y: e.clientY };

      timerRef.current = setTimeout(() => {
        firedRef.current = true;
        timerRef.current = null;
        navigator.vibrate?.(50);
        onLongPress();
      }, threshold);
    },
    [disabled, onLongPress, threshold]
  );

  const onPointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (!startPos.current) {
        return;
      }
      const dx = e.clientX - startPos.current.x;
      const dy = e.clientY - startPos.current.y;
      if (dx * dx + dy * dy > moveThreshold * moveThreshold) {
        cancel();
      }
    },
    [cancel, moveThreshold]
  );

  const onClick = useCallback((e: React.MouseEvent) => {
    if (firedRef.current) {
      e.preventDefault();
      e.stopPropagation();
      firedRef.current = false;
    }
  }, []);

  const onContextMenu = useCallback(
    (e: React.MouseEvent) => {
      if (disabled) {
        return;
      }
      e.preventDefault();
      if (!firedRef.current) {
        firedRef.current = true;
        onLongPress();
      }
    },
    [disabled, onLongPress]
  );

  return {
    handlers: {
      onPointerDown,
      onPointerUp: cancel,
      onPointerCancel: cancel,
      onPointerLeave: cancel,
      onPointerMove,
      onClick,
      onContextMenu
    },
    style: {
      touchAction: "manipulation",
      WebkitTouchCallout: "none"
    } as React.CSSProperties
  };
}
